import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Stack, List, ListItem, ListItemText,
  ListItemSecondaryAction, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert, Chip, CircularProgress, Divider, MenuItem
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import KeyIcon from '@mui/icons-material/Key';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import SecurityIcon from '@mui/icons-material/Security';
import DevicesIcon from '@mui/icons-material/Devices';
import CloudIcon from '@mui/icons-material/Cloud';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LinkIcon from '@mui/icons-material/Link';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import StorageProfiler from './StorageProfiler';
import UsageProfiler from './UsageProfiler';
import { isFirebaseSms, initRecaptcha, sendPhoneOtp, verifyPhoneOtp } from '../../../services/firebase';
import { connectDriveAccount } from '../../../utils/driveConnect';

// ── WebAuthn helpers ──────────────────────────────────────────────────────────
function b64urlToBuffer(base64url) {
  const base64 = (base64url || '').replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const buffer = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) buffer[i] = binary.charCodeAt(i);
  return buffer.buffer;
}

function bufferToB64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function credentialToJSON(cred) {
  const response = cred.response;
  const obj = { id: cred.id, rawId: bufferToB64url(cred.rawId), type: cred.type };
  if (response.clientDataJSON !== undefined)
    obj.response = {
      clientDataJSON: bufferToB64url(response.clientDataJSON),
      attestationObject: response.attestationObject ? bufferToB64url(response.attestationObject) : undefined,
      authenticatorData: response.authenticatorData ? bufferToB64url(response.authenticatorData) : undefined,
      signature: response.signature ? bufferToB64url(response.signature) : undefined,
      userHandle: response.userHandle ? bufferToB64url(response.userHandle) : undefined,
    };
  return obj;
}

// ── GIS loader ────────────────────────────────────────────────────────────────
function loadGis() {
  return new Promise((resolve) => {
    if (window.google?.accounts?.oauth2) { resolve(); return; }
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.onload = resolve;
    s.onerror = resolve;
    document.head.appendChild(s);
  });
}
// ─────────────────────────────────────────────────────────────────────────────

const AccountSettings = ({ customerId, credentials = [], onCredentialsChange, profileApi, phone, countryCode = '+44',
  driveConnected: initialDriveConnected, driveEmail: initialDriveEmail,
  googleEmail: initialGoogleEmail, hasAnthropicApiKey: initialHasAnthropicApiKey,
  anthropicApiKeyUpdatedAt: initialAnthropicApiKeyUpdatedAt,
  hasGeminiApiKey: initialHasGeminiApiKey,
  geminiApiKeyUpdatedAt: initialGeminiApiKeyUpdatedAt,
  plannerModel: initialPlannerModel, plannerModelOptions = [],
  faceGroupingModel: initialFaceGroupingModel,
  faceGroupingModelOptions = [],
  captionModel: initialCaptionModel,
  captionModelOptions: initialCaptionModelOptions = [],
  hasCaptionGeminiApiKey: initialHasCaptionGeminiApiKey,
  captionGeminiApiKeyUpdatedAt: initialCaptionGeminiApiKeyUpdatedAt,
  onDriveStatusChange, onAnthropicKeyChange, onFaceGroupingModelChange }) => {
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyError, setPasskeyError] = useState('');
  const [passkeySuccess, setPasskeySuccess] = useState('');
  const [nameDialog, setNameDialog] = useState(false);
  const [deviceName, setDeviceName] = useState('My Device');
  const [pendingCredential, setPendingCredential] = useState(null);

  // Password change
  const [pwDialog, setPwDialog] = useState(false);
  const [pwStep, setPwStep] = useState('otp');
  const [otp, setOtp] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  // Google Sign-In link
  const [googleEmail, setGoogleEmail] = useState(initialGoogleEmail || '');
  const [googleLinkLoading, setGoogleLinkLoading] = useState(false);
  const [googleLinkError, setGoogleLinkError] = useState('');
  const [googleLinkSuccess, setGoogleLinkSuccess] = useState('');
  const [googleUnlinkConfirm, setGoogleUnlinkConfirm] = useState(false);

  // Google Drive
  const [driveConnected, setDriveConnected] = useState(Boolean(initialDriveConnected));
  const [driveEmail, setDriveEmail] = useState(initialDriveEmail || '');
  const [driveLoading, setDriveLoading] = useState(false);
  const [driveError, setDriveError] = useState('');
  const [driveSuccess, setDriveSuccess] = useState('');

  // Planner API keys
  const [anthropicKey, setAnthropicKey] = useState('');
  const [hasAnthropicKey, setHasAnthropicKey] = useState(Boolean(initialHasAnthropicApiKey));
  const [anthropicUpdatedAt, setAnthropicUpdatedAt] = useState(initialAnthropicApiKeyUpdatedAt || null);
  const [hasGeminiKey, setHasGeminiKey] = useState(Boolean(initialHasGeminiApiKey));
  const [geminiUpdatedAt, setGeminiUpdatedAt] = useState(initialGeminiApiKeyUpdatedAt || null);
  const [anthropicLoading, setAnthropicLoading] = useState(false);
  const [anthropicError, setAnthropicError] = useState('');
  const [anthropicSuccess, setAnthropicSuccess] = useState('');
  const [plannerModel, setPlannerModel] = useState(initialPlannerModel || 'local:qwen2.5-3b');
  const [faceGroupingModel, setFaceGroupingModel] = useState(initialFaceGroupingModel || 'adaface');
  const [faceModelLoading, setFaceModelLoading] = useState(false);
  const [faceModelError, setFaceModelError] = useState('');
  const [faceModelSuccess, setFaceModelSuccess] = useState('');

  // Image captioning
  const [captionModel, setCaptionModel] = useState(initialCaptionModel || 'minicpm_v');
  const [captionModelOptions] = useState(initialCaptionModelOptions.length ? initialCaptionModelOptions : [
    { value: 'minicpm_v', label: 'MiniCPM-V 4.6 (Local)', backend: 'local', requires_api_key: false },
    { value: 'gemini_flash', label: 'Gemini Flash (Cloud)', backend: 'gemini', requires_api_key: true, api_key_provider: 'gemini_caption' },
  ]);
  const [captionGeminiKey, setCaptionGeminiKey] = useState('');
  const [hasCaptionGeminiKey, setHasCaptionGeminiKey] = useState(Boolean(initialHasCaptionGeminiApiKey));
  const [captionGeminiKeyUpdatedAt, setCaptionGeminiKeyUpdatedAt] = useState(initialCaptionGeminiApiKeyUpdatedAt || null);
  const [captionModelLoading, setCaptionModelLoading] = useState(false);
  const [captionModelError, setCaptionModelError] = useState('');
  const [captionModelSuccess, setCaptionModelSuccess] = useState('');

  const supportsWebAuthn = !!window.PublicKeyCredential;
  const selectedPlannerOption = (
    plannerModelOptions.find(option => option.value === plannerModel)
    || plannerModelOptions[0]
    || { value: 'local:qwen2.5-3b', backend: 'local', label: 'Local - Qwen 2.5 3B Instruct' }
  );
  const selectedKeyProvider = selectedPlannerOption.api_key_provider || (
    selectedPlannerOption.backend === 'claude' ? 'anthropic'
      : selectedPlannerOption.backend === 'gemini' ? 'gemini'
        : null
  );
  const selectedProviderLabel = selectedKeyProvider === 'gemini' ? 'Gemini' : 'Anthropic';
  const hasSelectedProviderKey = selectedKeyProvider === 'gemini' ? hasGeminiKey : hasAnthropicKey;
  const selectedProviderUpdatedAt = selectedKeyProvider === 'gemini' ? geminiUpdatedAt : anthropicUpdatedAt;

  const handleAnthropicSave = async () => {
    const apiKey = anthropicKey.trim();
    if (selectedPlannerOption.requires_api_key && !apiKey && !hasSelectedProviderKey) {
      setAnthropicError(`Enter a ${selectedProviderLabel} API key to use ${selectedPlannerOption.label}.`);
      return;
    }
    setAnthropicError('');
    setAnthropicSuccess('');
    setAnthropicLoading(true);
    try {
      const payload = { planner_model: plannerModel };
      if (apiKey) payload.api_key = apiKey;
      const result = await profileApi.updatePlannerSettings(payload);
      const updatedAt = apiKey ? new Date().toISOString() : selectedProviderUpdatedAt;
      setAnthropicKey('');
      setHasAnthropicKey(Boolean(result.has_anthropic_api_key));
      setHasGeminiKey(Boolean(result.has_gemini_api_key));
      if (selectedKeyProvider === 'gemini') setGeminiUpdatedAt(updatedAt);
      else setAnthropicUpdatedAt(updatedAt);
      setAnthropicSuccess('Search model settings saved.');
      onAnthropicKeyChange?.({
        planner_model: result.planner_model || plannerModel,
        planner_backend: result.planner_backend || selectedPlannerOption.backend,
        has_anthropic_api_key: Boolean(result.has_anthropic_api_key),
        has_gemini_api_key: Boolean(result.has_gemini_api_key),
        anthropic_api_key_updated_at: selectedKeyProvider === 'anthropic' ? updatedAt : anthropicUpdatedAt,
        gemini_api_key_updated_at: selectedKeyProvider === 'gemini' ? updatedAt : geminiUpdatedAt,
      });
    } catch (err) {
      setAnthropicError(err.message || 'Failed to save search model settings.');
    } finally {
      setAnthropicLoading(false);
    }
  };

  const handleAnthropicDelete = async () => {
    setAnthropicError('');
    setAnthropicSuccess('');
    setAnthropicLoading(true);
    try {
      if (selectedKeyProvider === 'gemini') await profileApi.deleteGeminiKey();
      else await profileApi.deleteAnthropicKey();
      setAnthropicKey('');
      if (selectedKeyProvider === 'gemini') {
        setHasGeminiKey(false);
        setGeminiUpdatedAt(null);
        setAnthropicSuccess('Gemini API key removed.');
        onAnthropicKeyChange?.({
          has_gemini_api_key: false,
          gemini_api_key_updated_at: null,
        });
      } else {
        setHasAnthropicKey(false);
        setAnthropicUpdatedAt(null);
        setAnthropicSuccess('Anthropic API key removed.');
        onAnthropicKeyChange?.({
          has_anthropic_api_key: false,
          anthropic_api_key_updated_at: null,
        });
      }
    } catch (err) {
      setAnthropicError(err.message || 'Failed to remove Anthropic API key.');
    } finally {
      setAnthropicLoading(false);
    }
  };

  const handleFaceModelSave = async () => {
    setFaceModelError('');
    setFaceModelSuccess('');
    setFaceModelLoading(true);
    try {
      const result = await profileApi.updateFaceGroupingSettings({ face_grouping_model: faceGroupingModel });
      setFaceModelSuccess('Default face grouping model saved.');
      onFaceGroupingModelChange?.({ face_grouping_model: result.face_grouping_model || faceGroupingModel });
    } catch (err) {
      setFaceModelError(err.message || 'Failed to save face grouping model.');
    } finally {
      setFaceModelLoading(false);
    }
  };

  const selectedCaptionOption = captionModelOptions.find(o => o.value === captionModel) || captionModelOptions[0];

  const handleCaptionSave = async () => {
    const apiKey = captionGeminiKey.trim();
    if (selectedCaptionOption?.requires_api_key && !apiKey && !hasCaptionGeminiKey) {
      setCaptionModelError('Enter a Gemini API key to use Gemini Flash captioning.');
      return;
    }
    setCaptionModelError('');
    setCaptionModelSuccess('');
    setCaptionModelLoading(true);
    try {
      const payload = { caption_model: captionModel };
      if (apiKey) payload.api_key = apiKey;
      const result = await profileApi.updateCaptionSettings(payload);
      setCaptionGeminiKey('');
      setHasCaptionGeminiKey(Boolean(result.has_caption_gemini_api_key));
      if (apiKey) setCaptionGeminiKeyUpdatedAt(new Date().toISOString());
      setCaptionModelSuccess('Image captioning settings saved.');
    } catch (err) {
      setCaptionModelError(err.message || 'Failed to save caption settings.');
    } finally {
      setCaptionModelLoading(false);
    }
  };

  const handleCaptionGeminiKeyDelete = async () => {
    setCaptionModelError('');
    setCaptionModelSuccess('');
    setCaptionModelLoading(true);
    try {
      await profileApi.deleteCaptionGeminiKey();
      setCaptionGeminiKey('');
      setHasCaptionGeminiKey(false);
      setCaptionGeminiKeyUpdatedAt(null);
      setCaptionModelSuccess('Caption Gemini key removed.');
    } catch (err) {
      setCaptionModelError(err.message || 'Failed to remove key.');
    } finally {
      setCaptionModelLoading(false);
    }
  };

  const handleRegisterPasskey = async () => {
    setPasskeyError('');
    setPasskeySuccess('');
    setPasskeyLoading(true);
    const timeoutId = setTimeout(() => {}, 10000);
    try {
      const options = await profileApi.webAuthnRegisterStart();
      const publicKey = {
        ...options,
        challenge: b64urlToBuffer(options.challenge),
        user: { ...options.user, id: b64urlToBuffer(options.user.id) },
        excludeCredentials: (options.excludeCredentials || []).map(c => ({ ...c, id: b64urlToBuffer(c.id) })),
      };
      const credential = await navigator.credentials.create({ publicKey });
      setPendingCredential(credentialToJSON(credential));
      setNameDialog(true);
    } catch (err) {
      if (err.name === 'AbortError' || (err.message || '').includes('aborted')) {
        setPasskeyError('Registration timed out. Please try again.');
      } else {
        setPasskeyError(err.message || 'Passkey registration failed');
      }
    } finally {
      clearTimeout(timeoutId);
      setPasskeyLoading(false);
    }
  };

  const handleNameConfirm = async () => {
    setPasskeyLoading(true);
    try {
      await profileApi.webAuthnRegisterComplete(pendingCredential, deviceName);
      setPasskeySuccess('Passkey registered successfully!');
      setNameDialog(false);
      if (phone) localStorage.setItem('passkey_phone', phone);
      onCredentialsChange();
    } catch (err) {
      setPasskeyError(err.message || 'Failed to save passkey');
    } finally {
      setPasskeyLoading(false);
      setPendingCredential(null);
    }
  };

  const handleDeleteCredential = async (id) => {
    try {
      await profileApi.deleteCredential(id);
      onCredentialsChange();
    } catch {
      setPasskeyError('Failed to remove passkey');
    }
  };

  // Pre-warm reCAPTCHA when the password-change dialog opens.
  useEffect(() => {
    if (pwDialog && isFirebaseSms() && phone) initRecaptcha();
  }, [pwDialog, phone]);

  const handleSendOtp = async () => {
    setPwLoading(true);
    setPwError('');
    try {
      if (isFirebaseSms() && phone) {
        await sendPhoneOtp(`${countryCode}${phone.replace(/\D/g, '')}`);
      } else {
        await profileApi.sendOtp(phone, countryCode);
      }
      setPwStep('new');
    } catch (err) {
      setPwError(err.message || 'Failed to send OTP. Try again.');
    } finally {
      setPwLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPw !== confirmPw) { setPwError('Passwords do not match'); return; }
    if (newPw.length < 8) { setPwError('Password must be at least 8 characters'); return; }
    setPwLoading(true);
    setPwError('');
    try {
      if (isFirebaseSms()) {
        const firebaseIdToken = await verifyPhoneOtp(otp);
        await profileApi.changePasswordFirebase(firebaseIdToken, newPw);
      } else {
        await profileApi.changePassword(otp, newPw);
      }
      setPwDialog(false);
      setPwStep('otp');
      setOtp(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      setPwError(err.message || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  const handleGoogleLink = async () => {
    setGoogleLinkError('');
    setGoogleLinkSuccess('');
    setGoogleLinkLoading(true);
    try {
      await loadGis();
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      if (!clientId) { setGoogleLinkError('Google Client ID not configured.'); return; }

      const accessToken = await new Promise((resolve, reject) => {
        const client = window.google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: 'openid email profile',
          callback: (tokenResponse) => {
            if (tokenResponse.error) reject(new Error(tokenResponse.error_description || tokenResponse.error));
            else resolve(tokenResponse.access_token);
          },
        });
        client.requestAccessToken({ prompt: 'consent' });
      });

      const result = await profileApi.linkGoogleAccount(accessToken);
      const linkedEmail = result.google_email || '';
      setGoogleEmail(linkedEmail);
      setGoogleLinkSuccess(`Google account linked: ${linkedEmail}`);
      onDriveStatusChange?.({ signin_email: linkedEmail });
    } catch (err) {
      setGoogleLinkError(err.message || 'Failed to link Google account.');
    } finally {
      setGoogleLinkLoading(false);
    }
  };

  const handleGoogleUnlink = async () => {
    setGoogleLinkError('');
    setGoogleLinkSuccess('');
    setGoogleLinkLoading(true);
    setGoogleUnlinkConfirm(false);
    try {
      await profileApi.unlinkGoogleAccount();
      setGoogleEmail('');
      setGoogleLinkSuccess('Google account unlinked.');
      onDriveStatusChange?.({ signin_email: null });
    } catch (err) {
      setGoogleLinkError(err.message || 'Failed to unlink Google account.');
    } finally {
      setGoogleLinkLoading(false);
    }
  };

  const handleDriveConnect = async () => {
    setDriveError('');
    setDriveSuccess('');
    setDriveLoading(true);
    try {
      // Stores an encrypted refresh token server-side - this lets CircleMoments
      // broker "uncompressed original" downloads (and, for the native app, the
      // backgroundable Drive-upload relay) for this organiser's Drive-sourced
      // events on demand, indefinitely, without prompting them again.
      const { driveEmail: linkedDriveEmail } = await connectDriveAccount();
      setDriveConnected(true);
      setDriveEmail(linkedDriveEmail || driveEmail);
      setDriveSuccess('Google Drive connected successfully!');
      onDriveStatusChange?.({ connected: true, drive_email: linkedDriveEmail || driveEmail });
    } catch (err) {
      setDriveError(err.message || 'Failed to connect Google Drive.');
    } finally {
      setDriveLoading(false);
    }
  };

  const handleDriveDisconnect = async () => {
    setDriveError('');
    setDriveLoading(true);
    try {
      await profileApi.disconnectDrive();
      setDriveConnected(false);
      setDriveEmail('');
      setDriveSuccess('Google Drive disconnected.');
      onDriveStatusChange?.({ connected: false, drive_email: null });
    } catch (err) {
      setDriveError(err.message || 'Failed to disconnect.');
    } finally {
      setDriveLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* ── Cloud Storage Connect ──────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
          <CloudIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Cloud Storage</Typography>
            <Typography variant="caption" color="text.secondary">
              Connect cloud storage to upload photos directly from the cloud for event grouping
            </Typography>
          </Box>
        </Stack>

        {driveError && <Alert severity="error" sx={{ mb: 1.5 }}>{driveError}</Alert>}
        {driveSuccess && <Alert severity="success" sx={{ mb: 1.5 }}>{driveSuccess}</Alert>}

        {/* Google Drive */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                component="img"
                src="https://www.gstatic.com/images/branding/product/1x/drive_2020q4_48dp.png"
                alt="Google Drive"
                sx={{ width: 32, height: 32 }}
              />
              <Box>
                <Typography variant="body1" fontWeight={600}>Google Drive</Typography>
                {driveConnected ? (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                    <Typography variant="caption" color="success.main">
                      Connected{driveEmail ? ` as ${driveEmail}` : ''}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Not connected - connect to use Drive photos in events
                  </Typography>
                )}
              </Box>
            </Stack>
            <Box sx={{ flexShrink: 0 }}>
              {driveConnected ? (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={driveLoading ? <CircularProgress size={14} /> : <LinkOffIcon />}
                  onClick={handleDriveDisconnect}
                  disabled={driveLoading}
                >
                  Disconnect
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={driveLoading ? <CircularProgress size={14} color="inherit" /> : <LinkIcon />}
                  onClick={handleDriveConnect}
                  disabled={driveLoading}
                  sx={{ bgcolor: '#1a73e8', '&:hover': { bgcolor: '#1558b0' } }}
                >
                  Connect Drive
                </Button>
              )}
            </Box>
          </Stack>
        </Paper>
      </Paper>

      {/* ── Google Sign-In ────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
          <DevicesIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Face Grouping</Typography>
            <Typography variant="caption" color="text.secondary">
              Choose the default face model for new event grouping runs
            </Typography>
          </Box>
        </Stack>

        {faceModelError && <Alert severity="error" sx={{ mb: 1.5 }}>{faceModelError}</Alert>}
        {faceModelSuccess && <Alert severity="success" sx={{ mb: 1.5 }}>{faceModelSuccess}</Alert>}

        <Stack spacing={1.5}>
          <TextField
            select
            label="Default face model"
            value={faceGroupingModel}
            onChange={e => {
              setFaceGroupingModel(e.target.value);
              setFaceModelError('');
              setFaceModelSuccess('');
            }}
            fullWidth
          >
            {(faceGroupingModelOptions.length ? faceGroupingModelOptions : [
              { value: 'adaface', label: 'AdaFace' },
              { value: 'insightface/buffalo_l', label: 'InsightFace buffalo_l' },
            ]).map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Alert severity="info">
            This affects new grouping runs. Add-more keeps the original event model. Auto-naming only compares same-model face embeddings, so a changed model may need one manual reassignment before future events learn that face for the new model.
          </Alert>
          <Box>
            <Button
              variant="contained"
              onClick={handleFaceModelSave}
              disabled={faceModelLoading}
              startIcon={faceModelLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            >
              Save face model
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
          <VpnKeyIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Search Model</Typography>
            <Typography variant="caption" color="text.secondary">
              Choose the language model used for moments search planning
            </Typography>
          </Box>
        </Stack>

        {anthropicError && <Alert severity="error" sx={{ mb: 1.5 }}>{anthropicError}</Alert>}
        {anthropicSuccess && <Alert severity="success" sx={{ mb: 1.5 }}>{anthropicSuccess}</Alert>}

        <Stack spacing={1.5}>
          <TextField
            select
            label="Model"
            value={plannerModel}
            onChange={e => {
              setPlannerModel(e.target.value);
              setAnthropicError('');
              setAnthropicSuccess('');
            }}
            fullWidth
          >
            {(plannerModelOptions.length ? plannerModelOptions : [selectedPlannerOption]).map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          {selectedPlannerOption.backend === 'local' && (
            <Alert severity="info">
              Local models are loaded on your first search after selection, so that first search can take longer while the model warms up.
            </Alert>
          )}

          <Stack direction="row" spacing={0.75} alignItems="center">
            {selectedPlannerOption.requires_api_key && hasSelectedProviderKey ? (
              <>
                <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography variant="body2" color="success.main" fontWeight={500}>
                  {selectedProviderLabel} key saved{selectedProviderUpdatedAt ? ` on ${new Date(selectedProviderUpdatedAt).toLocaleDateString()}` : ''}
                </Typography>
              </>
            ) : selectedPlannerOption.requires_api_key ? (
              <Typography variant="body2" color="text.secondary">No {selectedProviderLabel} API key saved</Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">No API key required for this model</Typography>
            )}
          </Stack>

          {selectedPlannerOption.requires_api_key && (
            <TextField
              label={hasSelectedProviderKey ? `Replace ${selectedProviderLabel} API key` : `${selectedProviderLabel} API key`}
              type="password"
              value={anthropicKey}
              onChange={e => setAnthropicKey(e.target.value)}
              fullWidth
              autoComplete="off"
              placeholder={selectedKeyProvider === 'gemini' ? 'AIza...' : 'sk-ant-...'}
            />
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="contained"
              startIcon={anthropicLoading ? <CircularProgress size={14} color="inherit" /> : <VpnKeyIcon />}
              onClick={handleAnthropicSave}
              disabled={anthropicLoading || (selectedPlannerOption.requires_api_key && !anthropicKey.trim() && !hasSelectedProviderKey)}
            >
              Save Model
            </Button>
            {selectedPlannerOption.requires_api_key && hasSelectedProviderKey && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleAnthropicDelete}
                disabled={anthropicLoading}
              >
                Remove Key
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* ── Image Captioning ──────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
          <CloudIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Image Captioning</Typography>
            <Typography variant="caption" color="text.secondary">
              Choose the AI model used to auto-generate photo captions in the Tags &amp; Notes panel
            </Typography>
          </Box>
        </Stack>

        {captionModelError && <Alert severity="error" sx={{ mb: 1.5 }}>{captionModelError}</Alert>}
        {captionModelSuccess && <Alert severity="success" sx={{ mb: 1.5 }}>{captionModelSuccess}</Alert>}

        <Stack spacing={1.5}>
          <TextField
            select
            label="Caption model"
            value={captionModel}
            onChange={e => {
              setCaptionModel(e.target.value);
              setCaptionModelError('');
              setCaptionModelSuccess('');
            }}
            fullWidth
          >
            {captionModelOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          {captionModel === 'minicpm_v' && (
            <Alert severity="info">
              MiniCPM-V runs via llama-server. Start it before generating captions:{' '}
              <code>llama-server -hf openbmb/MiniCPM-V-4.6-gguf:Q4_K_M</code>
              {' '}(set <code>CM_MINICPMV_SERVER_URL</code> if using a non-default port).
            </Alert>
          )}

          {selectedCaptionOption?.requires_api_key && (
            <Stack direction="row" spacing={0.75} alignItems="center">
              {hasCaptionGeminiKey ? (
                <>
                  <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                  <Typography variant="body2" color="success.main" fontWeight={500}>
                    Gemini key saved{captionGeminiKeyUpdatedAt ? ` on ${new Date(captionGeminiKeyUpdatedAt).toLocaleDateString()}` : ''}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">No Gemini API key saved for captioning</Typography>
              )}
            </Stack>
          )}

          {selectedCaptionOption?.requires_api_key && (
            <TextField
              label={hasCaptionGeminiKey ? 'Replace Gemini API key (captioning)' : 'Gemini API key (captioning)'}
              type="password"
              value={captionGeminiKey}
              onChange={e => setCaptionGeminiKey(e.target.value)}
              fullWidth
              autoComplete="off"
              placeholder="AIza..."
            />
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="contained"
              startIcon={captionModelLoading ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
              onClick={handleCaptionSave}
              disabled={captionModelLoading || (selectedCaptionOption?.requires_api_key && !captionGeminiKey.trim() && !hasCaptionGeminiKey)}
            >
              Save Caption Model
            </Button>
            {selectedCaptionOption?.requires_api_key && hasCaptionGeminiKey && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleCaptionGeminiKeyDelete}
                disabled={captionModelLoading}
              >
                Remove Key
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
          <Box
            component="img"
            src="https://www.gstatic.com/images/branding/product/1x/googleg_32dp.png"
            alt="Google"
            sx={{ width: 24, height: 24 }}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Google Sign-In</Typography>
            <Typography variant="caption" color="text.secondary">
              Link a Google account to sign in with "Continue with Google" on the login page
            </Typography>
          </Box>
        </Stack>

        {googleLinkError && <Alert severity="error" sx={{ mb: 1.5 }}>{googleLinkError}</Alert>}
        {googleLinkSuccess && <Alert severity="success" sx={{ mb: 1.5 }}>{googleLinkSuccess}</Alert>}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Box>
            {googleEmail ? (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography variant="body2" color="success.main" fontWeight={500}>
                  Linked as {googleEmail}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No Google account linked
              </Typography>
            )}
          </Box>

          {googleEmail ? (
            googleUnlinkConfirm ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">Remove Google sign-in?</Typography>
                <Button size="small" color="error" variant="contained" onClick={handleGoogleUnlink} disabled={googleLinkLoading}>
                  {googleLinkLoading ? <CircularProgress size={14} color="inherit" /> : 'Disconnect'}
                </Button>
                <Button size="small" onClick={() => setGoogleUnlinkConfirm(false)} disabled={googleLinkLoading}>Cancel</Button>
              </Stack>
            ) : (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<LinkOffIcon />}
                onClick={() => setGoogleUnlinkConfirm(true)}
                disabled={googleLinkLoading}
              >
                Disconnect
              </Button>
            )
          ) : (
            <Button
              variant="outlined"
              size="small"
              startIcon={googleLinkLoading ? <CircularProgress size={14} /> : (
                <svg width="16" height="16" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              onClick={handleGoogleLink}
              disabled={googleLinkLoading}
              sx={{ borderColor: '#dadce0', color: 'text.primary', '&:hover': { borderColor: '#bbb' } }}
            >
              Link Google Account
            </Button>
          )}
        </Stack>
      </Paper>

      {/* ── Passkeys section ───────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
          <SecurityIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Passkeys &amp; Biometrics</Typography>
            <Typography variant="caption" color="text.secondary">
              Use fingerprint, Face ID, or a hardware key for secure login
            </Typography>
          </Box>
        </Stack>

        {passkeyError && <Alert severity="error" sx={{ mb: 1 }}>{passkeyError}</Alert>}
        {passkeySuccess && <Alert severity="success" sx={{ mb: 1 }}>{passkeySuccess}</Alert>}

        {!supportsWebAuthn && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your browser does not support passkeys. Try Chrome, Safari, or Edge.
          </Alert>
        )}

        {credentials.length > 0 && (
          <List dense disablePadding sx={{ mb: 2 }}>
            {credentials.map(c => (
              <React.Fragment key={c.id}>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexGrow={1}>
                    <DevicesIcon fontSize="small" color="action" />
                    <ListItemText
                      primary={c.friendly_name}
                      secondary={`Added ${new Date(c.created_at).toLocaleDateString()}${c.last_used ? ` · Last used ${new Date(c.last_used).toLocaleDateString()}` : ''}`}
                    />
                    {c.backed_up && <Chip label="Synced" size="small" color="success" variant="outlined" />}
                  </Stack>
                  <ListItemSecondaryAction>
                    <IconButton size="small" color="error" onClick={() => handleDeleteCredential(c.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        )}

        {credentials.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary', mb: 2 }}>
            <FingerprintIcon sx={{ fontSize: 40, opacity: 0.3 }} />
            <Typography variant="body2">No passkeys registered yet</Typography>
          </Box>
        )}

        <Button
          variant="outlined"
          startIcon={passkeyLoading ? <CircularProgress size={16} /> : <KeyIcon />}
          onClick={handleRegisterPasskey}
          disabled={passkeyLoading || !supportsWebAuthn}
        >
          Register a Passkey
        </Button>
      </Paper>

      {/* ── Change password ────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
          <LockResetIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Change Password</Typography>
            <Typography variant="caption" color="text.secondary">
              Verify your phone number then set a new password
            </Typography>
          </Box>
        </Stack>
        <Button variant="outlined" onClick={() => { setPwDialog(true); setPwStep('otp'); setPwError(''); }}>
          Change Password
        </Button>
      </Paper>

      {/* ── Storage profiler ───────────────────────────────────────────────── */}
      <StorageProfiler profileApi={profileApi} />

      {/* ── Compute usage profiler ─────────────────────────────────────────── */}
      <UsageProfiler profileApi={profileApi} />

      {/* ── Dialogs ───────────────────────────────────────────────────────── */}
      <Dialog open={nameDialog} onClose={() => setNameDialog(false)} maxWidth="xs">
        <DialogTitle>Name This Device</DialogTitle>
        <DialogContent>
          <TextField label="Device name" value={deviceName} onChange={e => setDeviceName(e.target.value)}
            fullWidth autoFocus sx={{ mt: 1 }} placeholder="e.g. MacBook Pro, iPhone" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleNameConfirm} disabled={passkeyLoading}>
            {passkeyLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pwDialog} onClose={() => setPwDialog(false)} maxWidth="xs">
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {pwError && <Alert severity="error">{pwError}</Alert>}
            {pwStep === 'otp' ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  We'll send a verification code to your phone{phone ? ` ending in ${phone.slice(-4)}` : ''}.
                </Typography>
                <Button variant="contained" onClick={handleSendOtp} disabled={pwLoading}>
                  {pwLoading ? <CircularProgress size={20} /> : 'Send OTP'}
                </Button>
              </>
            ) : (
              <>
                <TextField label="OTP from SMS" value={otp} onChange={e => setOtp(e.target.value)} fullWidth />
                <TextField label="New Password" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} fullWidth />
                <TextField label="Confirm Password" type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} fullWidth />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwDialog(false)}>Cancel</Button>
          {pwStep === 'new' && (
            <Button variant="contained" onClick={handleChangePassword} disabled={pwLoading}>
              {pwLoading ? <CircularProgress size={20} /> : 'Update Password'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountSettings;
